// Delivery date/timezone utilities — formatting, cutoff/lock calculation, and
// week grouping, all anchored to the meal-plan app's single delivery timezone.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import { DELIVERY_DAYS, LOCK_HOURS } from 'constants/meals';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

const DELIVERY_TIMEZONE = 'America/New_York';

export const formatDeliveryDate = (dateString) => {
    if (dateString === 'No Date') return 'No Date';

    try {
        // Parse the date and format it in Eastern Time
        return dayjs.tz(dateString, DELIVERY_TIMEZONE).format('ddd MMM D');
    } catch {
        return dateString;
    }
};

export const parseEstDate = (dateString) => {
    // Parse date string and return as dayjs object in Eastern Time
    return dayjs.tz(dateString, DELIVERY_TIMEZONE);
};

export const sortDeliveryDates = (dates) => {
    return Array.from(dates).sort((a, b) => {
        if (a === 'No Date') return 1;
        if (b === 'No Date') return -1;
        return parseEstDate(a).valueOf() - parseEstDate(b).valueOf();
    });
};

export const getCutoffTime = (deliveryDate) => {
    // Parse delivery date in Eastern Time
    const deliveryDateObj = dayjs.tz(deliveryDate, DELIVERY_TIMEZONE);

    // Get the day of week (0 = Sunday, 1 = Monday, etc.)
    const deliveryDay = deliveryDateObj.day();

    if (deliveryDay === DELIVERY_DAYS.MONDAY) { // Monday delivery
        // Lock on Saturday at 2 PM EST
        const lockDate = deliveryDateObj
            .subtract(2, 'day') // Go back 2 days (Monday -> Saturday)
            .hour(LOCK_HOURS.MONDAY_DELIVERY)
            .minute(0)
            .second(0)
            .millisecond(0);

        return lockDate.toDate();

    } else if (deliveryDay === DELIVERY_DAYS.TUESDAY) { // Tuesday delivery
        // Lock on Saturday at 2 PM EST
        const lockDate = deliveryDateObj
            .subtract(3, 'day') // Go back 3 days (Tuesday -> Saturday)
            .hour(LOCK_HOURS.TUESDAY_DELIVERY)
            .minute(0)
            .second(0)
            .millisecond(0);
        return lockDate.toDate();
    } else if (deliveryDay === DELIVERY_DAYS.WEDNESDAY) { // Wednesday delivery
        // Lock on Tuesday at 12 PM EST (noon)
        const lockDate = deliveryDateObj
            .subtract(1, 'day') // Go back 1 day (Wednesday -> Tuesday)
            .hour(LOCK_HOURS.WEDNESDAY_DELIVERY)
            .minute(0)
            .second(0)
            .millisecond(0);

        return lockDate.toDate();

    } else if (deliveryDay === DELIVERY_DAYS.THURSDAY) { // Thursday delivery
        // Lock on Tuesday at 12 PM EST (noon)
        const lockDate = deliveryDateObj
            .subtract(2, 'day') // Go back 2 days (Thursday -> Tuesday)
            .hour(LOCK_HOURS.THURSDAY_DELIVERY)
            .minute(0)
            .second(0)
            .millisecond(0);

        return lockDate.toDate();

    } else if (deliveryDay === DELIVERY_DAYS.SUNDAY) { // Sunday delivery
        // Lock on Friday at 10 PM EST
        const lockDate = deliveryDateObj
            .subtract(2, 'day') // Go back 2 days (Sunday -> Friday)
            .hour(LOCK_HOURS.SUNDAY_DELIVERY)
            .minute(0)
            .second(0)
            .millisecond(0);

        return lockDate.toDate()
    } else {
        const lockDate = deliveryDateObj
            .subtract(2, 'day') // Go back 2 days
            .hour(LOCK_HOURS.OTHER_DELIVERY)
            .minute(0)
            .second(0)
            .millisecond(0);
        return lockDate.toDate();
    }
};

// Helper function to format lock date (Date object)
export const formatLockDate = (lockDate) => {
    if (!lockDate) return '';

    return dayjs.tz(lockDate, DELIVERY_TIMEZONE).format('MMM D, h:mm A');
};

export const getDayOfWeek = (date) => {
    return dayjs.tz(date, DELIVERY_TIMEZONE).format('dddd');
};

/** Weekday names at their dayjs `.day()` index — the sort order for delivery
 *  slots. Lowercase; callers normalize. */
export const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** "Sun, Sep 1" for an already-resolved "YYYY-MM-DD" delivery date (resolved
 *  once server-side, per resolveSlotDeliveryDate in backend/server.js). */
export const formatConcreteDeliveryDate = (dateStr) => {
    if (!dateStr) return '';
    return dayjs.tz(dateStr, DELIVERY_TIMEZONE).format('ddd, MMM D');
};

/**
 * Get week key for a given date (Sunday-Saturday weeks)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} The week's Sunday as "YYYY-MM-DD" (e.g., "2026-11-01")
 */
export const getWeekKey = (dateString) => {
    if (!dateString || dateString === 'No Date') return null;

    // UTC because dayjs.tz keeps a stale offset when .day() crosses a DST change, misplacing the week.
    return dayjs.utc(dateString).day(0).format('YYYY-MM-DD');
};

/**
 * Get week bounds (Sunday-Saturday) for a given week key
 * @param {string} weekKey - The week's Sunday as "YYYY-MM-DD"
 * @returns {object} Object with display labels { start: "MMM D", end: "MMM D" }
 */
export const getWeekBounds = (weekKey) => {
    if (!weekKey) return null;

    const start = dayjs.utc(weekKey);
    const end = start.add(6, 'day');

    return {
        start: start.format('MMM D'),
        end: end.format('MMM D')
    };
};

/**
 * Group an array of dates by their week
 * @param {Array<string>} dates - Array of date strings in YYYY-MM-DD format
 * @returns {object} Object mapping week keys to arrays of dates
 */
export const groupDatesByWeek = (dates) => {
    if (!dates || !Array.isArray(dates)) return {};

    const grouped = {};

    dates.forEach(date => {
        if (date === 'No Date') return; // Skip invalid dates

        const weekKey = getWeekKey(date);
        if (!weekKey) return;

        if (!grouped[weekKey]) {
            grouped[weekKey] = [];
        }
        grouped[weekKey].push(date);
    });

    // Sort dates within each week
    Object.keys(grouped).forEach(weekKey => {
        grouped[weekKey].sort((a, b) => {
            return dayjs.tz(a, DELIVERY_TIMEZONE).valueOf() -
                dayjs.tz(b, DELIVERY_TIMEZONE).valueOf();
        });
    });
    return grouped;
};

/**
 * Format week key into human-readable date range
 * @param {string} weekKey - The week's Sunday as "YYYY-MM-DD"
 * @returns {string} Formatted week range (e.g., "Jan 19 - Jan 25")
 */
export const formatWeekHeader = (weekKey) => {
    if (!weekKey) return '';

    const bounds = getWeekBounds(weekKey);
    if (!bounds) return weekKey;

    return `Week of ${bounds.start} - ${bounds.end}`;
};

/**
 * Check if a date falls on a valid delivery day (Sunday - Thursday)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} True if date is Sun-Thu, false otherwise
 */
export const isValidDeliveryDayInWeek = (dateString) => {
    if (!dateString || dateString === 'No Date') return false;

    const date = dayjs.tz(dateString, DELIVERY_TIMEZONE);
    const dayOfWeek = date.day(); // 0 = Sunday, 6 = Saturday

    // Valid delivery days: Sunday (0) through Thursday (4)
    return dayOfWeek >= 0 && dayOfWeek <= 4;
};

export const RESUME_WEEKS = 26;

const DAY_INDEX = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };

/**
 * Dates a skipped subscription can resume on: every week after `fromDate`, on the
 * subscription's own delivery weekday (YYYY-MM-DD strings).
 */
export const getResumeDates = (fromDate, deliveryDay, weeks = RESUME_WEEKS) => {
    const base = new Date(`${fromDate}T12:00:00`);
    const target = DAY_INDEX[deliveryDay];
    if (target != null) base.setDate(base.getDate() + ((target - base.getDay() + 7) % 7));
    const out = [];
    for (let i = 1; i <= weeks; i += 1) {
        const d = new Date(base);
        d.setDate(d.getDate() + i * 7);
        out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    return out;
};
